import React, { createContext, useContext, useState, useEffect } from 'react';
import { login, register, getMe, switchCompany as switchCompanyService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isStorefrontBuild = process.env.REACT_APP_PUBLIC_SITE === 'true';
    const isStorefrontPath = window.location.pathname === '/' ||
      window.location.pathname.startsWith('/store/') ||
      window.location.pathname.startsWith('/product/') ||
      window.location.pathname.startsWith('/blog') ||
      window.location.pathname.startsWith('/hakkimizda') ||
      window.location.pathname.startsWith('/yasal/') ||
      window.location.pathname.startsWith('/profile') ||
      window.location.pathname.startsWith('/quotes/');

    // Don't fetch CRM user on auth pages or public storefront routes
    const isAuthPage = window.location.pathname === '/login' || 
                      window.location.pathname === '/register' ||
                      window.location.pathname.startsWith('/public/');
    
    if (isAuthPage || (!isStorefrontBuild && isStorefrontPath)) {
      setLoading(false);
      return;
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      getMe()
        .then(data => {
          setUser(data.user);
          setCompany(data.user.company);
        })
        .catch((error) => {
          // Only clear token if it's a 401 (unauthorized), not network errors
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const setAuthFromResponse = (response) => {
    const { token, user } = response || {};
    if (!token || !user) {
      return;
    }
    localStorage.setItem('token', token);
    setUser(user);
    setCompany(user.company);
  };

  const loginUser = async (credentials) => {
    const response = await login(credentials);
    if (response.twoFactorRequired || response.twoFactorSetupRequired) {
      localStorage.removeItem('token');
      return response;
    }
    setAuthFromResponse(response);
    return response;
  };

  const switchCompany = async (companyId) => {
    const response = await switchCompanyService(companyId);
    const { token, user } = response;
    localStorage.setItem('token', token);
    setUser(user);
    setCompany(user.company);
    // Reload to ensure all company-scoped data is refetched
    window.location.reload();
    return response;
  };

  const registerUser = async (userData) => {
    const response = await register(userData);
    setAuthFromResponse(response);
    return response;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setCompany(null);
  };

  const refreshUser = async () => {
    try {
      const data = await getMe();
      setUser(data.user);
      setCompany(data.user.company);
      return data;
    } catch (error) {
      console.error('User refresh error:', error);
      throw error;
    }
  };

  const updateCompanyInfo = (updatedCompany) => {
    setCompany(updatedCompany);
  };

  const value = {
    user,
    company,
    login: loginUser,
    register: registerUser,
    completeLogin: setAuthFromResponse,
    logout,
    loading,
    refreshUser,
    updateCompanyInfo,
    switchCompany
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 